import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import styled from "styled-components";
import {toast} from "react-toastify";

export default function VideoUploader({
  file,
  onSelect,
  filePath,
  readOnly = false
}) {
  const inputRef = useRef(null);

  const [previewUrl, setPreviewUrl] =
      useState("");

  useEffect(() => {
    if (file) {
      const objectUrl =
          URL.createObjectURL(file);

      setPreviewUrl(objectUrl);

      return () => {
        URL.revokeObjectURL(
            objectUrl
        );
      };
    }

    setPreviewUrl(
        filePath || ""
    );

    if (inputRef.current) {
      inputRef.current.value = "";
    }

    return undefined;
  }, [file, filePath]);

  const displayFileName =
      useMemo(() => {
        if (file?.name) {
          return file.name;
        }

        if (!filePath) {
          return "";
        }

        try {
          const pathname =
              new URL(filePath).pathname;

          return decodeURIComponent(
              pathname
              .split("/")
              .filter(Boolean)
              .pop() ?? ""
          );
        } catch {
          return decodeURIComponent(
              filePath
              .split("?")[0]
              .split("/")
              .filter(Boolean)
              .pop() ?? filePath
          );
        }
      }, [file, filePath]);

  const fileSizeText =
      file?.size
          ? `${(
              file.size /
              1024 /
              1024
          ).toFixed(1)} MB`
          : null;

  const hasAudio =
      Boolean(previewUrl);

  const handleOpenFileDialog = () => {
    if (readOnly) {
      return;
    }

    inputRef.current?.click();
  };

  const handleFileChange = event => {
    const selectedFile =
        event.target.files?.[0] ??
        null;

    if (!selectedFile) {
      return;
    }

    if (
        !selectedFile.type.startsWith(
            "audio/"
        )
    ) {
      toast.warning(
          "오디오 파일만 업로드할 수 있습니다.",
          {
            toastId:
                "audioFileTypeError"
          }
      );

      event.target.value = "";
      return;
    }

    onSelect(selectedFile);
  };

  const handleReset = () => {
    if (readOnly) {
      return;
    }

    onSelect(null);

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleInputClick = event => {
    /*
     * 동일한 파일을 다시 선택해도
     * change 이벤트가 발생하도록 초기화한다.
     */
    event.currentTarget.value = "";
  };

  return (
      <UploaderContainer>
        <HiddenFileInput
            ref={inputRef}
            type="file"
            accept="audio/*"
            disabled={readOnly}
            onClick={
              handleInputClick
            }
            onChange={
              handleFileChange
            }
        />

        <UploadHeader>
          <UploadInfo>
            <UploadLabel>
              오디오 소재
            </UploadLabel>

            <UploadGuide>
              MP3 등 오디오 형식의 파일을
              등록할 수 있습니다.
            </UploadGuide>
          </UploadInfo>

          {!readOnly && (
              <HeaderActions>
                <SelectFileButton
                    type="button"
                    onClick={
                      handleOpenFileDialog
                    }
                >
                  {hasAudio
                      ? "파일 변경"
                      : "파일 선택"}
                </SelectFileButton>

                {file && (
                    <ResetButton
                        type="button"
                        onClick={
                          handleReset
                        }
                        aria-label="선택한 오디오 파일 초기화"
                    >
                      <ResetIcon
                          aria-hidden="true"
                      >
                        ↻
                      </ResetIcon>

                      초기화
                    </ResetButton>
                )}
              </HeaderActions>
          )}
        </UploadHeader>

        {hasAudio ? (
            <AudioPreviewCard>
              <AudioPlayer
                  src={previewUrl}
                  controls
                  preload="metadata"
              />

              <FileInformation>
                <FileIcon>
                  ♪
                </FileIcon>

                <FileTextArea>
                  <FileName
                      title={
                        displayFileName
                      }
                  >
                    {displayFileName ||
                        "등록된 오디오 파일"}
                  </FileName>

                  <FileMeta>
                    {file
                        ? "새로 선택한 파일"
                        : "현재 등록된 파일"}

                    {fileSizeText &&
                        ` · ${fileSizeText}`}
                  </FileMeta>
                </FileTextArea>

                <RegisteredBadge
                    $newFile={
                      Boolean(file)
                    }
                >
                  {file
                      ? "변경 예정"
                      : "등록됨"}
                </RegisteredBadge>
              </FileInformation>
            </AudioPreviewCard>
        ) : (
            <EmptyUploadArea
                $readOnly={readOnly}
                onClick={
                  handleOpenFileDialog
                }
            >
              <EmptyIcon>
                ♪
              </EmptyIcon>

              <EmptyTitle>
                등록된 오디오 파일이
                없습니다.
              </EmptyTitle>

              {!readOnly && (
                  <EmptyDescription>
                    파일 선택 버튼을 눌러
                    오디오 소재를 등록해주세요.
                  </EmptyDescription>
              )}
            </EmptyUploadArea>
        )}
      </UploaderContainer>
  );
}

const UploaderContainer = styled.div`
  width: 100%;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadHeader = styled.div`
  display: flex;
  min-height: 40px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 14px;
`;

const UploadInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UploadLabel = styled.div`
  color: #444;
  font-size: 13px;
  font-weight: 700;
`;

const UploadGuide = styled.div`
  color: #999;
  font-size: 11px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const SelectFileButton = styled.button`
  min-width: 82px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: #ffe8d2;
  }
`;

const ResetButton = styled.button`
  display: inline-flex;
  height: 36px;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background-color: #fff;
  color: #666;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    border-color: #f5811f;
    background-color: #fffaf6;
    color: #f5811f;
  }
`;

const ResetIcon = styled.span`
  font-size: 17px;
  line-height: 1;
`;

const AudioPreviewCard = styled.div`
  padding: 18px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #fff;
`;

const AudioPlayer = styled.audio`
  display: block;
  width: 100%;
  height: 44px;
  margin-bottom: 16px;
`;

const FileInformation = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
  padding-top: 14px;
  border-top: 1px solid #eee;
`;

const FileIcon = styled.div`
  display: inline-flex;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 18px;
  font-weight: 700;
`;

const FileTextArea = styled.div`
  min-width: 0;
  flex: 1;
`;

const FileName = styled.div`
  overflow: hidden;
  margin-bottom: 5px;
  color: #333;
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileMeta = styled.div`
  color: #999;
  font-size: 11px;
`;

const RegisteredBadge = styled.span`
  display: inline-flex;
  min-height: 24px;
  flex: 0 0 auto;
  align-items: center;
  padding: 0 9px;
  border: 1px solid
    ${({$newFile}) =>
    $newFile
        ? "#f1d4c2"
        : "#cce8d4"};
  border-radius: 12px;
  background-color:
    ${({$newFile}) =>
    $newFile
        ? "#fff4e8"
        : "#edf8f0"};
  color:
    ${({$newFile}) =>
    $newFile
        ? "#f5811f"
        : "#2b8a3e"};
  font-size: 10px;
  font-weight: 600;
`;

const EmptyUploadArea = styled.div`
  display: flex;
  min-height: 170px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px dashed #d8d8d8;
  border-radius: 8px;
  background-color: #fff;
  cursor:
    ${({$readOnly}) =>
    $readOnly
        ? "default"
        : "pointer"};

  &:hover {
    border-color:
      ${({$readOnly}) =>
    $readOnly
        ? "#d8d8d8"
        : "#f5811f"};
    background-color:
      ${({$readOnly}) =>
    $readOnly
        ? "#fff"
        : "#fffaf6"};
  }
`;

const EmptyIcon = styled.div`
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 20px;
  font-weight: 700;
`;

const EmptyTitle = styled.div`
  color: #555;
  font-size: 13px;
  font-weight: 600;
`;

const EmptyDescription = styled.div`
  color: #999;
  font-size: 11px;
`;